import type { GroupedOption } from '@ksyos/design-system';
import type { SingleValue } from 'react-select';
import { AsyncSelect } from '@ksyos/design-system';
import { useState } from 'react';
import styled from 'styled-components';
import type { Icd10Code } from '@common/interfaces/icd10';
import { createLabel, getDescriptionFromLabel, UNKNOWN_DIAGNOSIS_CODE } from '@common/interfaces/icd10';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import { getIcd10Codes } from '@frontend/services/api/icd10ApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';

export type Icd10SelectProps = Readonly<{
    error?: string;
    onChange?: (value: Icd10Code | null) => void;
    specialismCode?: string;
    value: Icd10Code | null;
}>;

/*
 * Inspired by https://icd.who.int/browse10/2019/en and https://trex.dhd.nl/
 */
export function Icd10Select({ error, onChange, specialismCode, value }: Icd10SelectProps) {
    const abortSignal = useAbortSignal();
    const [isLoading, setIsLoading] = useState(false);

    return (
        <StyledAsyncSelect
            defaultOptions={true}
            error={error}
            isClearable={true}
            isLoading={isLoading}
            loadingMessage={() => getSelectedLanguage().common.loading}
            loadOptions={loadOptions}
            onChange={(item: SingleValue<GroupedOption>) => {
                onChange?.(
                    item
                        ? {
                              code: item.value,
                              description:
                                  item.value === UNKNOWN_DIAGNOSIS_CODE
                                      ? item.label
                                      : getDescriptionFromLabel(item.label),
                          }
                        : null
                );
            }}
            onInputChange={(inputValue) => {
                if (!inputValue) {
                    return;
                }

                onChange?.({
                    code: UNKNOWN_DIAGNOSIS_CODE,
                    description: inputValue,
                });
            }}
            placeholder=""
            value={
                value
                    ? {
                          label: createLabel(value),
                          value: value.code,
                      }
                    : null
            }
        />
    );

    async function loadOptions(inputValue: string): Promise<GroupedOption[]> {
        setIsLoading(true);
        const result = await getIcd10Codes(
            {
                limit: 100,
                search: inputValue,
                specialismCode,
            },
            abortSignal
        );
        setIsLoading(false);

        // Grouped options
        const groupedOptions: GroupedOption[] = [];
        for (const [code, icd10Codes] of Object.entries(result.items)) {
            const isParentItem = !icd10Codes[0].code.includes('.');

            if (isParentItem) {
                const parentItem = nonNullable(icd10Codes.shift());
                groupedOptions.push({
                    isGroupHeader: true,
                    label: createLabel(parentItem),
                    value: parentItem.code,
                });
            } else {
                groupedOptions.push({
                    isDisabled: true,
                    isGroupHeader: true,
                    label: code,
                    value: code,
                });
            }

            for (const icd10Code of icd10Codes) {
                groupedOptions.push({
                    isGroupOption: true,
                    label: createLabel(icd10Code),
                    value: icd10Code.code,
                });
            }
        }

        if (inputValue) {
            groupedOptions.unshift({
                label: inputValue,
                value: UNKNOWN_DIAGNOSIS_CODE,
            });
        }

        return groupedOptions;
    }
}

const StyledAsyncSelect = styled(AsyncSelect<GroupedOption>)`
    label {
        font-weight: ${({ theme }) => theme.fontWeights.normal};
    }
`;
